import React, { useEffect, useState } from 'react'
import { Text, VStack } from '@chakra-ui/react'

import { useClinic } from '~contexts/clinic-context'
import SharedStack from '~components/shared/wrappers/shared-stack'
import {
    ClientFieldsFragment,
    ClientInfo,
    FilterOp,
    PatientInfo,
    useQueryClinicClientsLazyQuery,
} from '~graphql/generated/graphql'
import ClientItem from './client-item'
import { Spinner } from '@chakra-ui/react'
import { dedupeArray } from '~utils/helpers'

interface Props {
    searchString: string
    clientInfo: ClientInfo
    patientInfo: PatientInfo
    setClient: (clients: ClientFieldsFragment) => void
}

export default function ClientSearchResults({
    searchString,
    clientInfo,
    patientInfo,
    setClient,
}: Props) {
    const { clinic } = useClinic()
    const [isLoading, setIsLoading] = useState(false)
    const [searchResults, setSearchResults] = useState<ClientFieldsFragment[]>(
        []
    )

    const [queryClients] = useQueryClinicClientsLazyQuery({
        fetchPolicy: 'network-only',
    })

    useEffect(() => {
        async function getClients() {
            setIsLoading(true)
            const firstNameRes = await queryClients({
                variables: {
                    clinicId: clinic?.id || '',
                    where: [
                        {
                            column: 'clinic',
                            filterOp: FilterOp.Eq,
                            value: {
                                string: clinic?.id || '',
                            },
                        },
                        {
                            column: 'full_info_tsvector',
                            filterOp: FilterOp.Fts,
                            value: {
                                string: searchString
                                    .trim()
                                    .split(' ')
                                    .map(s => `${s}:*`)
                                    .join(' & '),
                            },
                        },
                        {
                            column: 'pms_id',
                            filterOp: FilterOp.Is,
                            value: {
                                string: null,
                            },
                            not: true,
                        },
                        {
                            column: 'is_temp_client',
                            filterOp: FilterOp.Eq,
                            value: {
                                boolean: false,
                            },
                        },
                    ],
                },
            })

            const lastNameRes = await queryClients({
                variables: {
                    clinicId: clinic?.id || '',
                    where: [
                        {
                            column: 'clinic',
                            filterOp: FilterOp.Eq,
                            value: {
                                string: clinic?.id || '',
                            },
                        },
                        {
                            column: 'last_name',
                            filterOp: FilterOp.Ilike,
                            value: {
                                string: `%${searchString}%`,
                            },
                        },
                        {
                            column: 'pms_id',
                            filterOp: FilterOp.Is,
                            value: {
                                string: null,
                            },
                            not: true,
                        },
                        {
                            column: 'is_temp_client',
                            filterOp: FilterOp.Eq,
                            value: {
                                boolean: false,
                            },
                        },
                    ],
                },
            })

            setSearchResults(
                dedupeArray(
                    [
                        ...(firstNameRes?.data?.queryClinicClients || []),
                        ...(lastNameRes?.data?.queryClinicClients || []),
                    ],
                    'id'
                ) as ClientFieldsFragment[]
            )
            setIsLoading(false)
        }

        if (searchString) getClients()
        else setSearchResults([])
    }, [searchString])

    return (
        <SharedStack>
            {isLoading ? (
                <Spinner
                    alignSelf="center"
                    size="lg"
                    my={6}
                    thickness="3px"
                    color="brand.500"
                    emptyColor="gray.200"
                />
            ) : searchResults.length > 0 ? (
                <>
                    <Text fontWeight="medium" fontSize="sm">
                        Showing results for "{searchString}"
                    </Text>
                    <VStack spacing={4} w="full">
                        {searchResults.map(client => (
                            <ClientItem
                                key={client.id}
                                client={client}
                                clientInfo={clientInfo}
                                patientInfo={patientInfo}
                                setClient={setClient}
                            />
                        ))}
                    </VStack>
                </>
            ) : (
                <>
                    <Text alignSelf="center" fontWeight="semibold" my={6}>
                        No results found for {searchString}
                    </Text>
                </>
            )}
        </SharedStack>
    )
}
